<template>
    <div>
        <slot :duplications="form.busy" :show="show" name="activator" />
        <b-modal
                ref="modal"
                :ok-disabled="form.busy"
                :title="$t('Duplicate')"
                ok-only
                size="lg"
                v-bind="$attrs"
                @ok.prevent="handleDuplicate"
                @show="eager ? fetchDuplicationTargets() : null"
        >
            <form @submit.prevent="handleDuplicate">
                <slot :form="form" :pendingTargets="pendingTargets" :possibleTargets="possibleTargets">
                    <template v-if="!onlyLocations">
                        <b-form-group
                                :invalid-feedback="form.errors.get('name')"
                                :label="$t('Name')"
                                label-for="new-name"
                        >
                            <b-form-input
                                    id="new-name"
                                    v-model="form.name"
                                    :state="form.errors.has('name') ? false : null"
                                    required
                            />
                        </b-form-group>

                        <b-form-group
                                :invalid-feedback="form.errors.get('short_name')"
                                :label="$t('Short name')"
                                label-for="new-short_name"
                        >
                            <b-form-input
                                    id="new-short_name"
                                    v-model="form.short_name"
                                    :state="form.errors.has('short_name') ? false : null"
                            />
                        </b-form-group>

                        <b-form-group
                                v-if="showOverrideOptions"
                                :label="$t('How to handle duplication errors')"
                                label-for="override"
                        >
                            <b-form-radio-group
                                    id="override"
                                    v-model="form.override"
                                    :options="overrideOptions"
                                    stacked
                            />
                        </b-form-group>

                        <slot :form="form" name="form" />

                        <slot :options="form" name="options">
                            <hr>

                            <b-form-group label-for="deep">
                                <b-form-checkbox
                                        id="deep"
                                        v-model="form.deep"
                                >
                                    {{ $t('Duplicate with dependencies') }}
                                </b-form-checkbox>

                                <b-form-checkbox
                                        id="services"
                                        v-model="form[DUPLICATE_WITH_SERVICES]"
                                        :disabled="!form.deep"
                                        class="ml-3"
                                >
                                    {{ $t('Duplicate with services') }}
                                </b-form-checkbox>

                                <b-form-checkbox
                                        id="elements"
                                        v-model="form[DUPLICATE_WITH_ELEMENTS]"
                                        :disabled="!form.deep"
                                        class="ml-3"
                                >
                                    {{ $t('Duplicate with elements') }}
                                </b-form-checkbox>

                              <b-form-checkbox
                                  v-if="showCloneOptions"
                                  :id="DUPLICATE_CLONE_ELEMENTS"
                                  v-model="form[DUPLICATE_CLONE_ELEMENTS]"
                                  :disabled="!form[DUPLICATE_WITH_ELEMENTS]"
                                  class="ml-3"
                              >
                                {{ $t('Clone elements') }}
                              </b-form-checkbox>

                              <b-form-checkbox
                                  v-if="showCloneOptions"
                                  :id="DUPLICATE_CLONE_FOUNDATION"
                                  v-model="form[DUPLICATE_CLONE_FOUNDATION]"
                                  class="ml-3"
                              >
                                {{ $t('Clone foundations') }}
                              </b-form-checkbox>
                            </b-form-group>
                        </slot>
                    </template>
                    <hr>
                    <b-form-group label-for="duplicateMirror">
                        <b-form-row>
                            <b-col>
                                <b-form-checkbox
                                    id="duplicateMirror"
                                    v-model="form.mirror"
                                >
                                    {{ $t('Duplicate as Mirror') }}
                                </b-form-checkbox>
                            </b-col>
                            <b-col cols="auto">
                                <b-button id="duplicateHelp" variant="primary-outline">
                                    <fa icon="question" class="text-primary" />
                                </b-button>
                                <b-tooltip target="duplicateHelp" triggers="blur click">
                                    {{ $t('Duplicate Mirror help') }}
                                </b-tooltip>
                            </b-col>
                        </b-form-row>
                    </b-form-group>
                    <template v-if="showLocations">
                        <template v-if="!onlyLocations">
                            <hr>

                            <b-form-group label-for="duplicateToOtherLocations">
                                <b-form-checkbox
                                        id="duplicateToOtherLocations"
                                        v-model="duplicateToOtherLocations"
                                        switch
                                        @input="$event ? fetchDuplicationTargets() : null"
                                >
                                    {{ locationsMessage }}
                                </b-form-checkbox>
                            </b-form-group>
                        </template>

                        <loading-bar
                                v-if="pendingTargets"
                                :absolute="false"
                        />

                        <div
                                v-if="duplicateToOtherLocations || onlyLocations"
                                :class="{minHeight: showLocations && projectId}"
                        >
                            <b-form-group
                                    v-if="projectId"
                                    :label="$t('Floor')"
                                    label-for="floor_id"
                            >
                                <b-input-group class="align-items-start">
                                    <floor-select
                                            id="floor_id"
                                            v-model="floor"
                                            :resource="`projects/${projectId}/floors`"
                                            class="w-auto"
                                            group-label="name"
                                            group-values="children"
                                            no-custom-slots
                                    />

                                    <b-input-group-append>
                                        <b-button @click="floor = null">
                                            <fa :title="$t('Clear')" icon="times" />
                                        </b-button>
                                    </b-input-group-append>
                                </b-input-group>
                            </b-form-group>

                            <tree-view
                                    v-if="!pendingTargets && possibleTargets.length"
                                    v-model="form.targets"
                                    :items="possibleTargets"
                                    :errors="form.errors"
                                    class="mt-3"
                                    error-prefix="targets"
                            />

                            <no-results-found
                                    v-if="!pendingTargets && !possibleTargets.length"
                                    class="mt-3"
                            />
                        </div>
                    </template>
                </slot>

              <input type="submit" hidden>
            </form>

            <template #modal-ok>
                <b-spinner v-if="form.busy" small />
                <template v-else>
                    {{ $t('Duplicate') }}
                </template>
            </template>
      </b-modal>
    </div>
</template>
<script>
    import Form from 'vform';
    import TreeView from "@/components/TreeView";
    import FloorSelect from '@/components/selects/ResourceSelect';

    import {
        DUPLICATE_WITH_ELEMENTS,
        DUPLICATE_WITH_SERVICES,
        DUPLICATE_CLONE_ELEMENTS,
        DUPLICATE_CLONE_FOUNDATION,
        OVERRIDE_ALL,
        OVERRIDE_DEPENDENCIES, OVERRIDE_NO
    } from '@/constants/index'

    export default {
        name: 'DuplicateModal',
        components: {TreeView, FloorSelect},
        props: {
            apiUrl: {
                type: String,
                required: true,
            },
            projectId: {
              type: [Number, String],
              default: null
            },
            value: {
                type: Object,
                default: () => ({})
            },
            locationsMessage: {
                type: String,
                default() {
                    return this.$t('Duplicate to other location');
                },
            },
            hideLocations: {
                type: Boolean,
                default: false
            },
            eager: {
                type: Boolean,
                default: false
            },
            showOverrideOptions: {
                type: Boolean,
                default: false
            },
            showCloneOptions: {
                type: Boolean,
                default: false
            },
            onlyLocations: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                duplicateToOtherLocations: false,
                pendingTargets: false,
                form: new Form(
                    Object.assign({}, {
                        name: '',
                        targets: [],
                        deep: true,
                        withServices: true,
                        withElements: true,
                        override: null,
                        mirror: false
                    }, this.value)
                ),
                floor: null,
                possibleTargets: [],
                DUPLICATE_WITH_SERVICES,
                DUPLICATE_WITH_ELEMENTS,
                DUPLICATE_CLONE_ELEMENTS,
                DUPLICATE_CLONE_FOUNDATION,
                overrideOptions: [
                    {
                        text: this.$t(OVERRIDE_NO),
                        value: null
                    },
                    {
                        text: this.$t(OVERRIDE_DEPENDENCIES),
                        value: OVERRIDE_DEPENDENCIES
                    },
                    {
                        text: this.$t(OVERRIDE_ALL),
                        value: OVERRIDE_ALL
                    },
                ]
            };
        },
        computed: {
            showLocations() {
                return !this.hideLocations;
            }
        },
        watch: {
            'form.deep'(newDeepValue) {
                if (!newDeepValue) {
                    this.form.withServices = false;
                    this.form.withElements = false;
                }
            },
            'form.busy'(duplicating) {
                this.$emit('duplicating', duplicating);
            },

            'floor': 'fetchDuplicationTargets'
        },
        methods: {
            fetchDuplicationTargets() {
                if (this.pendingTargets) {
                    return;
                }

                if (this.projectId && !this.floor) {
                    this.possibleTargets = [];
                    return;
                }

                this.pendingTargets = true;

                this.axios
                    .get(this.apiUrl, { params: { floor_id: this.floor ? this.floor.id : null}})
                    .then(({data}) => this.possibleTargets = Object.freeze(data))
                    .catch(e => e.response || throw e)
                    .finally(() => this.pendingTargets = false)
            },

            handleDuplicate() {
                if (this.form.busy) {
                    return;
                }
                this.form
                    .post(this.apiUrl)
                    .then((response) => {
                        this.$refs.modal.hide()

                        if (response.data && response.data.message) {
                          this.successToast(response.data.message);
                        }

                        if (response.status === 201 && response.data) {
                            this.$emit('duplicated', response.data)
                        }
                    })
                    .catch(e => e.response || throw e)
            },
            show() {
                this.$refs.modal.show();
            },
        }
    };
</script>

<style scoped>
    >>> .minHeight {
        min-height: 310px;
    }

    fieldset >>> > div {
        background-color: rgba(0, 0, 0, 0.1);
    }

    fieldset {
        padding: 0;
        margin: 0;
    }

    fieldset > div > div {
        width: 33.33333333%;
        padding-left: 28px;
        display: inline-block;
    }
</style>
